import { OfferType } from "@/services/offer.service";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, DollarSign, FileText, Check, X, Building2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface OfferCardProps {
  offer: OfferType;
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
  isAccepting?: boolean;
  isDeclining?: boolean;
}

const OfferCard = ({
  offer,
  onAccept,
  onDecline,
  isAccepting,
  isDeclining,
}: OfferCardProps) => {
  const statusColors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
    declined: "bg-rose-100 text-rose-700 border-rose-200",
    expired: "bg-slate-100 text-slate-700 border-slate-200",
  };

  const isPending = offer.status === "pending";

  return (
    <Card className="overflow-hidden border-none shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-indigo-100 transition-all duration-300 rounded-[2rem] bg-white/80 backdrop-blur-sm group">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <Badge className={cn("rounded-lg border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider", statusColors[offer.status] || "bg-slate-100")}>
              {offer.status}
            </Badge>
            <CardTitle className="text-xl font-black text-slate-800 pt-2">
              {offer.internship_title || `Internship Offer`}
            </CardTitle>
            {offer.company_name && (
              <p className="text-sm font-bold text-indigo-600 flex items-center gap-1.5">
                <Building2 size={14} />
                {offer.company_name}
              </p>
            )}
          </div>
          <div className="h-12 w-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform duration-300">
            <Check size={24} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
              <Calendar size={10} /> Duration
            </p>
            <p className="text-sm font-bold text-slate-700">
              {format(new Date(offer.start_date), "MMM d")} - {format(new Date(offer.end_date), "MMM d, yyyy")}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
              <DollarSign size={10} /> Stipend
            </p>
            <p className="text-sm font-black text-slate-700">
              {offer.stipend > 0 ? `₹${offer.stipend.toLocaleString()}/mo` : "Unpaid"}
            </p>
          </div>
        </div>

        {offer.offer_letter_url && (
          <a 
            href={offer.offer_letter_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors text-sm font-bold group/link"
          >
            <FileText size={18} className="text-slate-400 group-hover/link:text-indigo-600" />
            View Offer Letter
          </a>
        )}

        {isPending ? (
          <div className="flex gap-3 pt-2">
            <Button 
              onClick={() => onAccept(offer.id)} 
              disabled={isAccepting || isDeclining}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-6 font-black shadow-lg shadow-indigo-100 hover:scale-[1.02] transition-all"
            >
              {isAccepting ? "Accepting..." : "Accept Offer"}
            </Button>
            <Button 
              variant="outline"
              onClick={() => onDecline(offer.id)} 
              disabled={isAccepting || isDeclining}
              className="flex-1 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 rounded-xl py-6 font-black transition-all"
            >
              {isDeclining ? "Declining..." : "Decline"}
            </Button>
          </div>
        ) : offer.status === "accepted" ? (
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-center text-sm font-black flex items-center justify-center gap-2">
            <Check size={18} />
            You accepted this offer
          </div>
        ) : offer.status === "declined" ? (
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-slate-500 text-center text-sm font-black flex items-center justify-center gap-2">
            <X size={18} />
            Offer Declined
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-slate-400 text-center text-sm font-black italic">
            This offer has expired
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OfferCard;
